import { createRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Route as AdminLayoutRoute } from "./_layout";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import { DataTable, type Column } from "@/components/application/data-table/data-table";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Modal } from "@/components/application/modals/modal-controlled";
import { useToast } from "@/components/application/notifications/toast-provider";
import {
  useCreateTenant,
  useTenantList,
  useUpdateTenant,
  type TenantRow,
} from "../../hooks/useTenantQueries";

/**
 * Registr tenantů (RLS root entity, princip #6). globalAdmin zakládá interní
 * a přefakturační subjekty (UC-005 — A&L soft). Tenant se po založení napojí na
 * Středisko (/admin/billing-groups) a pozve se mu tenantAdmin (/admin/user-invites).
 */

const TENANT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "internal", label: "Interní (Expertní služby)" },
  { value: "reinvoicing_subject", label: "Přefakturační subjekt" },
  { value: "group", label: "Skupina" },
];

function tenantTypeLabel(type: string): string {
  return TENANT_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type;
}

function TenantsPage() {
  const { data, isLoading, error } = useTenantList();
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<TenantRow | null>(null);

  const rows = data?.rows ?? [];

  const columns = useMemo<Column<TenantRow>[]>(
    () => [
      {
        key: "name",
        header: "Název",
        cell: (r) => <strong className="text-primary">{r.display_name}</strong>,
      },
      {
        key: "type",
        header: "Typ",
        cell: (r) => (
          <Badge color={r.tenant_type === "internal" ? "blue" : "gray"}>
            {tenantTypeLabel(r.tenant_type)}
          </Badge>
        ),
      },
      {
        key: "status",
        header: "Stav",
        cell: (r) =>
          r.deactivated_at ? (
            <Badge color="warning">Deaktivován</Badge>
          ) : (
            <Badge color="success">Aktivní</Badge>
          ),
      },
      {
        key: "actions",
        header: "Akce",
        cell: (r) => (
          <Button size="sm" color="secondary" onPress={() => setEditTarget(r)} data-testid={`tenant-edit-${r.id}`}>
            Upravit
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <section aria-labelledby="tenants-title" className="max-w-7xl mx-auto space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-1">
          <h2 id="tenants-title" className="text-2xl font-bold text-primary m-0">
            Tenanti
          </h2>
          <p className="text-base text-tertiary m-0">
            Datové hranice (RLS) — interní organizace a přefakturační subjekty.
          </p>
        </div>
        <Button color="primary" onPress={() => setCreateOpen(true)} data-testid="tenant-add">
          Přidat tenanta
        </Button>
      </header>

      {error && (
        <InlineAlert variant="error">
          {error instanceof Error ? error.message : "Načtení tenantů selhalo."}
        </InlineAlert>
      )}

      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold text-primary m-0">
            Seznam tenantů
            {!isLoading && <span className="ml-2 text-sm font-normal text-tertiary">({rows.length})</span>}
          </h3>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <LoadingIndicator label="Načítání…" />
          ) : (
            <DataTable
              caption="Registr tenantů"
              columns={columns}
              rows={rows}
              rowKey={(r) => r.id}
              emptyMessage="Zatím žádní tenanti."
            />
          )}
        </CardBody>
      </Card>

      {createOpen && (
        <TenantModal
          onClose={() => setCreateOpen(false)}
          onSaved={() => setCreateOpen(false)}
        />
      )}
      {editTarget && (
        <TenantModal
          existing={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => setEditTarget(null)}
        />
      )}
    </section>
  );
}

interface TenantModalProps {
  existing?: TenantRow;
  onClose: () => void;
  onSaved: () => void;
}

function TenantModal({ existing, onClose, onSaved }: TenantModalProps) {
  const isEdit = existing != null;
  const [displayName, setDisplayName] = useState(existing?.display_name ?? "");
  const [tenantType, setTenantType] = useState(existing?.tenant_type ?? "reinvoicing_subject");
  const [formError, setFormError] = useState<string | null>(null);
  const createTenant = useCreateTenant();
  const updateTenant = useUpdateTenant();
  const toast = useToast();

  async function submit() {
    if (displayName.trim().length < 2) {
      setFormError("Název musí mít alespoň 2 znaky.");
      return;
    }
    setFormError(null);
    const payload = { display_name: displayName.trim(), tenant_type: tenantType };
    try {
      if (isEdit) {
        await updateTenant.mutateAsync({ id: existing.id, payload });
        toast.push({ variant: "success", message: `Tenant „${displayName.trim()}" upraven.` });
      } else {
        await createTenant.mutateAsync(payload);
        toast.push({ variant: "success", message: `Tenant „${displayName.trim()}" vytvořen.` });
      }
      onSaved();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Uložení selhalo.");
    }
  }

  const submitting = createTenant.isPending || updateTenant.isPending;

  return (
    <Modal
      open
      title={isEdit ? `Upravit tenanta — ${existing.display_name}` : "Přidat tenanta"}
      onClose={submitting ? () => {} : onClose}
      footer={
        <>
          <Button color="secondary" onPress={onClose} isDisabled={submitting}>
            Zrušit
          </Button>
          <Button
            color="primary"
            onPress={() => void submit()}
            isDisabled={submitting}
            isLoading={submitting}
            data-testid="tenant-save"
          >
            {isEdit ? "Uložit" : "Vytvořit"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError && <InlineAlert variant="error">{formError}</InlineAlert>}

        <Input
          label="Název"
          isRequired
          hint="Obchodní název subjektu (např. A&L soft s.r.o.)."
          value={displayName}
          onChange={setDisplayName}
          isDisabled={submitting}
          data-testid="tenant-name"
        />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="tenant-type-select" className="text-sm font-medium text-secondary">
            Typ tenanta <span className="text-brand-tertiary">*</span>
          </label>
          <select
            id="tenant-type-select"
            className="block w-full min-h-touch rounded-lg bg-primary px-3 py-2 text-md text-primary ring-1 ring-inset ring-primary outline-hidden focus:ring-2 focus:ring-brand disabled:opacity-50"
            value={tenantType}
            onChange={(e) => setTenantType(e.target.value)}
            disabled={submitting}
            data-testid="tenant-type"
          >
            {TENANT_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </Modal>
  );
}

export const Route = createRoute({
  getParentRoute: () => AdminLayoutRoute,
  path: "/admin/tenants",
  component: TenantsPage,
});
